import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { usePopup } from '../../contexts/PopupContext';
import { supabase } from '../../lib/supabase';
import SingleEvent from '../../classes/events/SingleEvent';
import { useEventRealtimeData } from '../../hooks/useEventRealtimeData';
import { getAvatarUrl } from '../../utils/avatarUtils';
import { getAccountAvatarPlaceholderVariant } from '../../utils/accountAvatarStatus';
import { AccountAvatarPlaceholder } from '../common/AccountAvatarPlaceholder';
import { getFriendStatus } from '../../utils/friendsService';
import { getProfileCardDisplayName } from '../../utils/profileDisplayName';
import { getCachedEventStatus, setCachedEventStatus } from '../../utils/eventCardCache';
import { getEventCategoryVisual } from '../../config/eventCategoryVisuals';
import { EventCardIcon } from './EventCardIcons';
import { PopularCategoryIcon } from './PopularCategoryIcons';
import './EventCard.css';

let eventCardAuthUser = null;
let eventCardAuthUserPromise = null;

const isSupabaseAuthLockError = (err) => {
  const message = err?.message || String(err || '');
  return (
    err?.name === 'AbortError' ||
    message.includes('auth-token') ||
    message.includes('Lock broken') ||
    message.includes('stole it')
  );
};

const getEventCardAuthUser = async () => {
  if (eventCardAuthUser) {
    return eventCardAuthUser;
  }

  if (!eventCardAuthUserPromise) {
    eventCardAuthUserPromise = supabase.auth
      .getUser()
      .then(({ data }) => {
        eventCardAuthUser = data?.user || null;
        return eventCardAuthUser;
      })
      .catch((err) => {
        if (!isSupabaseAuthLockError(err)) {
          console.error('Error loading auth user for event card:', err);
        }
        return null;
      })
      .finally(() => {
        eventCardAuthUserPromise = null;
      });
  }

  return eventCardAuthUserPromise;
};

function EventCard({ event, isHost: isUserHost = false }) {
  const { t } = useTranslation(['events', 'discover']);
  const { openPopup } = usePopup();
  const [currentUser, setCurrentUser] = useState(null);
  const [isParticipant, setIsParticipant] = useState(false);
  const [hasPendingInvitation, setHasPendingInvitation] = useState(false);
  const [hasPendingApplication, setHasPendingApplication] = useState(false);
  const [friendStatusMap, setFriendStatusMap] = useState({});
  const [hasFriendParticipating, setHasFriendParticipating] = useState(false);
  const [hostFriendAccepted, setHostFriendAccepted] = useState(false);
  
  // Ref do śledzenia czy dane zostały już załadowane z serwera
  const hasLoadedFromServer = useRef(false);

  // Użyj hooka do zarządzania danymi w real-time
  const { event: eventData, host, participantsCount, participants } = useEventRealtimeData(event.id, event);

  // Pobierz użytkownika i załaduj cached data
  useEffect(() => {
    let cancelled = false;

    const initUser = async () => {
      const user = await getEventCardAuthUser();
      if (cancelled) return;

      setCurrentUser(user);
      
      // Załaduj dane z cache natychmiast po uzyskaniu użytkownika
      if (user?.id && event?.id) {
        const cached = getCachedEventStatus(user.id, event.id);
        if (cached) {
          setIsParticipant(cached.isParticipant ?? false);
          setHasPendingInvitation(cached.hasPendingInvitation ?? false);
          setHasPendingApplication(cached.hasPendingApplication ?? false);
          setHasFriendParticipating(cached.hasFriendParticipating ?? false);
        }
      }
    };
    
    initUser();

    return () => {
      cancelled = true;
    };
  }, [event?.id]);

  // Sprawdź czy użytkownik jest hostem (używane w useEffect poniżej)
  const isHost = currentUser && eventData?.host_id === currentUser.id;

  useEffect(() => {
    if (!host?.id || !currentUser?.id) {
      setHostFriendAccepted(false);
      return;
    }
    if (currentUser.id === host.id) {
      setHostFriendAccepted(true);
      return;
    }
    let cancelled = false;
    getFriendStatus(currentUser.id, host.id).then((status) => {
      if (!cancelled) setHostFriendAccepted(status === 'accepted');
    });
    return () => {
      cancelled = true;
    };
  }, [currentUser?.id, host?.id]);

  // Główny effect do ładowania wszystkich statusów i zapisywania do cache
  useEffect(() => {
    const loadAllStatuses = async () => {
      if (!currentUser?.id || !eventData?.id) {
        return;
      }

      try {
        // Równoległe zapytania dla lepszej wydajności
        const [participantResult, invitationResult, applicationResult] = await Promise.all([
          // Sprawdź uczestnictwo
          SingleEvent.isParticipantInDB(eventData.id, currentUser.id).catch(() => false),
          
          // Sprawdź zaproszenie
          supabase
            .from('event_invitations')
            .select('id')
            .eq('event_id', eventData.id)
            .eq('invitee_id', currentUser.id)
            .eq('status', 'pending')
            .maybeSingle()
            .then(({ data }) => !!data)
            .catch(() => false),
          
          // Sprawdź zgłoszenie
          supabase
            .from('event_applications')
            .select('id')
            .eq('event_id', eventData.id)
            .eq('user_id', currentUser.id)
            .eq('status', 'pending')
            .maybeSingle()
            .then(({ data }) => !!data)
            .catch(() => false)
        ]);

        setIsParticipant(participantResult);
        setHasPendingInvitation(invitationResult);
        setHasPendingApplication(applicationResult);
        
        hasLoadedFromServer.current = true;
      } catch (err) {
        console.error('Error loading event statuses:', err);
      }
    };

    loadAllStatuses();
  }, [currentUser?.id, eventData?.id]);

  // Pobierz status znajomości dla każdego uczestnika
  useEffect(() => {
    const loadFriendStatuses = async () => {
      if (!currentUser?.id) {
        setFriendStatusMap({});
        setHasFriendParticipating(false);
        return;
      }

      const statusMap = {};
      
      // Sprawdź status znajomości dla każdego uczestnika równolegle
      const friendStatusPromises = participants.map(async (participant) => {
        if (!participant.user?.id) return null;
        // Nie sprawdzaj siebie
        if (participant.user.id === currentUser.id) return null;
        
        try {
          const status = await getFriendStatus(currentUser.id, participant.user.id);
          return { userId: participant.user.id, status };
        } catch (err) {
          console.error(`Error checking friend status for user ${participant.user.id}:`, err);
          return { userId: participant.user.id, status: 'none' };
        }
      });

      const results = await Promise.all(friendStatusPromises);
      let foundFriend = false;
      
      results.forEach((result) => {
        if (result) {
          statusMap[result.userId] = result.status;
          if (result.status === 'accepted') {
            foundFriend = true;
          }
        }
      });

      setFriendStatusMap(statusMap);
      setHasFriendParticipating(foundFriend);
    };

    loadFriendStatuses();
  }, [currentUser?.id, participants]);

  // Zapisz do cache gdy dane się zmienią (tylko po załadowaniu z serwera)
  useEffect(() => {
    if (!currentUser?.id || !eventData?.id || !hasLoadedFromServer.current) return;
    
    setCachedEventStatus(currentUser.id, eventData.id, {
      isParticipant,
      hasPendingInvitation,
      hasPendingApplication,
      hasFriendParticipating
    });
  }, [currentUser?.id, eventData?.id, isParticipant, hasPendingInvitation, hasPendingApplication, hasFriendParticipating]);

  // Create SingleEvent instance for formatting
  const eventInstance = eventData ? new SingleEvent(eventData.id, eventData) : null;
  if (!eventInstance) return null;

  const daysUntil = eventInstance.getDaysUntilEvent();
  const unknownUser = t('events.details.unknownUser');

  const hostNameAllowReal =
    isParticipant ||
    isHost ||
    (currentUser?.id && host?.id === currentUser.id) ||
    hostFriendAccepted;
  const hostCardLabel = host
    ? getProfileCardDisplayName(host, {
        allowRealName: hostNameAllowReal,
        nameNotSetLabel: unknownUser,
      })
    : '';

  // Funkcja do obliczania statusu wydarzenia na podstawie daty i czasu
  const getEventStatus = () => {
    if (!eventData) return 'upcoming';
    
    // Sprawdź najpierw czy wydarzenie jest anulowane
    if (eventData.is_cancelled === true) {
      return 'cancelled';
    }
    
    const now = new Date();
    const eventStartDateTime = new Date(`${eventData.event_date}T${eventData.start_time}`);
    
    // Oblicz czas zakończenia na podstawie czasu trwania
    const eventEndDateTime = new Date(eventStartDateTime);
    eventEndDateTime.setMinutes(eventEndDateTime.getMinutes() + (eventData.duration_minutes || 0));
    
    // Sprawdź czy wydarzenie się zakończyło
    if (now >= eventEndDateTime) {
      return 'ended';
    }
    
    // Sprawdź czy wydarzenie jest w trakcie
    if (now >= eventStartDateTime) {
      return 'inProgress';
    }
    
    // Sprawdź czy wydarzenie rozpoczyna się wkrótce (w ciągu godziny)
    const oneHourFromNow = new Date(now.getTime() + 60 * 60 * 1000);
    if (eventStartDateTime <= oneHourFromNow) {
      return 'startingSoon';
    }
    
    // Domyślnie nadchodzące
    return 'upcoming';
  };

  const eventStatus = getEventStatus();
  const categoryVisual = getEventCategoryVisual(eventData.event_type);
  
  // Mapowanie statusów do klas CSS (konwersja camelCase na kebab-case)
  const getStatusClassName = (status) => {
    const classMap = {
      'upcoming': 'upcoming',
      'startingSoon': 'starting-soon',
      'inProgress': 'in-progress',
      'ended': 'ended',
      'cancelled': 'cancelled',
      'active': 'active' // dla kompatybilności wstecznej
    };
    return classMap[status] || 'active';
  };

  const handleCardClick = () => {
    openPopup('event-details', { eventId: event.id }, 0);
  };

  const handleHostClick = (e) => {
    e.stopPropagation();
    if (host) {
      openPopup('profile', { userId: host.id }, 0);
    }
  };

  // Mapowanie typów eventów do kolorów tagów
  const eventTypeColors = {
    'sport': '#ffedd5',
    'party': '#f3e8ff',
    'gaming': '#e0e7ff',
    'hangout': '#fef3c7',
    'trip': '#fffbeb',
    'other': '#f3f4f6'
  };

  const eventTypeTextColors = {
    'sport': '#c2410c',
    'party': '#7c3aed',
    'gaming': '#4338ca',
    'hangout': '#d97706',
    'trip': '#b45309',
    'other': '#4b5563'
  };

  const eventTypeLabel = t(`events.createEvent.eventTypes.${eventData.event_type}`)
    .replace(/^(?:☕|🏃|✨|🎮|🎉|🚗)\s*/u, '')
    .toUpperCase();

  const membershipType = isHost ? 'host' : isParticipant ? 'participant' : null;
  const maxParticipantSlots = Number(eventData.max_participants) || 0;
  const visibleParticipantSlots = Math.min(6, maxParticipantSlots);
  const hiddenParticipantSlots = Math.max(0, maxParticipantSlots - visibleParticipantSlots);

  return (
    <div className={`event-card event-card--${getStatusClassName(eventStatus)}`} onClick={handleCardClick}>
      <PopularCategoryIcon variant={categoryVisual.key} className="event-card-category-bg" />

      {/* Górna część - tagi */}
      <div className="event-card-top">
        <div className="event-card-tags-left">
          <span 
            className="event-type-tag"
            style={{
              backgroundColor: eventTypeColors[eventData.event_type] || '#f3f4f6',
              color: eventTypeTextColors[eventData.event_type] || '#4b5563'
            }}
          >
            <span>{eventTypeLabel}</span>
          </span>
        </div>
        <div className="event-card-tags-right">
          {!isHost && !isParticipant && hasPendingInvitation && (
            <span className="event-pending-invitation-tag">
              <EventCardIcon type="mail" className="event-tag-icon-svg" />
              <span>{t('events.card.pendingInvitation', 'Zaproszenie oczekuje')}</span>
            </span>
          )}
          {!isHost && !isParticipant && !hasPendingInvitation && hasPendingApplication && (
            <span className="event-pending-application-tag">
              <EventCardIcon type="hourglass" className="event-tag-icon-svg" />
              <span>{t('events.card.pendingApplication', 'Zgłoszenie oczekuje')}</span>
            </span>
          )}
          {hasFriendParticipating && (
            <span className="event-friend-participating-tag">
              <EventCardIcon type="friend" className="event-tag-icon-svg" />
              <span>{t('events.card.friendParticipating', 'Znajomy uczestniczy')}</span>
            </span>
          )}
        </div>
      </div>

      {/* Tytuł */}
      <h3 className="event-card-title">{eventData.title}</h3>

      {/* Szczegóły eventu */}
      <div className="event-card-details">
        {/* Host */}
        {host && (
          <div className="event-detail-item">
            <span className="event-detail-icon">
              <EventCardIcon type="user" className="event-detail-icon-svg" />
            </span>
            <button 
              className="event-detail-value-link"
              onClick={handleHostClick}
              type="button"
            >
              {hostCardLabel}
            </button>
          </div>
        )}

        {/* Data */}
        <div className="event-detail-item">
          <span className="event-detail-icon">
            <EventCardIcon type="calendar" className="event-detail-icon-svg" />
          </span>
          <span className="event-detail-value">
            {new Date(eventData.event_date + 'T00:00:00').toLocaleDateString('pl-PL', { day: 'numeric', month: 'short', year: 'numeric' })}
          </span>
        </div>

        {/* Czas */}
        <div className="event-detail-item">
          <span className="event-detail-icon">
            <EventCardIcon type="clock" className="event-detail-icon-svg" />
          </span>
          <span className="event-detail-value">{eventData.start_time}</span>
        </div>

        {/* Countdown */}
        {daysUntil > 0 && (
          <div className="event-detail-item">
            <span className="event-detail-icon">
              <EventCardIcon type="alarm" className="event-detail-icon-svg" />
            </span>
            <span className="event-countdown-badge">
              {t('events.info.inDays', { count: daysUntil })}
            </span>
          </div>
        )}

        {/* Lokalizacja */}
        <div className="event-detail-item event-detail-item-wide">
          <span className="event-detail-icon">
            <EventCardIcon type="location" className="event-detail-icon-svg" />
          </span>
          <span className="event-detail-value">{eventData.location}</span>
        </div>

        {(isParticipant || isHost) && eventData.additional_instructions?.trim() && (
          <div className="event-detail-item event-detail-item-wide event-detail-item-instructions">
            <span className="event-detail-icon" title={t('events.card.additionalInstructions', 'Dodatkowe instrukcje')}>
              <EventCardIcon type="clipboard" className="event-detail-icon-svg" />
            </span>
            <span
              className="event-detail-value event-card-instructions-text"
              title={eventData.additional_instructions.trim()}
            >
              {eventData.additional_instructions.trim()}
            </span>
          </div>
        )}
      </div>

      {/* Linia oddzielająca */}
      <div className="event-card-divider"></div>

      {/* Dolna część */}
      <div className="event-card-footer">
        {/* Uczestnicy */}
        <div className="event-card-participants">
          <div className="event-participants-info">
            <span className="event-participants-icon">
              <EventCardIcon type="users" className="event-participants-icon-svg" />
            </span>
            <span className="event-participants-count">
              {participantsCount}/{eventData.max_participants}
            </span>
          </div>
          <div className="event-participants-avatars">
            {/* Awatary uczestników */}
            {participants.slice(0, visibleParticipantSlots).map((participant) => {
              const user = participant.user;
              const accountPh = getAccountAvatarPlaceholderVariant(user);
              const accountPhTitle =
                accountPh === 'banned'
                  ? t('events.details.avatarBannedAria')
                  : t('events.details.avatarInactiveAria');
              const allowRealName =
                isParticipant ||
                isHost ||
                (currentUser?.id && user?.id === currentUser.id) ||
                friendStatusMap[user?.id] === 'accepted';
              const displayName = getProfileCardDisplayName(user, {
                allowRealName,
                nameNotSetLabel: unknownUser,
              });
              const initial = displayName.charAt(0).toUpperCase() || '?';
              const avatarUrl =
                !accountPh && user?.avatar_url ? getAvatarUrl(user.avatar_url, 100) : null;
              
              // Sprawdź czy powinien być rozmyty
              // Rozmyj tylko jeśli:
              // - użytkownik NIE jest uczestnikiem (isParticipant === false)
              // - użytkownik NIE jest hostem (isHost === false)
              // - uczestnik NIE jest hostem eventu (isParticipantHost === false)
              // - uczestnik NIE jest aktualnym użytkownikiem
              // - uczestnik NIE jest znajomym (friendStatus !== 'accepted')
              const isCurrentUser = currentUser?.id === user?.id;
              const isParticipantHost = user?.id === eventData?.host_id || user?.id === host?.id;
              const isFriend = friendStatusMap[user?.id] === 'accepted';
              const shouldBlur = !isParticipant && 
                                !isHost && 
                                !isParticipantHost && 
                                !isCurrentUser && 
                                !isFriend;
              
              return (
                <div
                  key={participant.id}
                  className={`event-participant-avatar ${shouldBlur ? 'blurred' : ''} ${accountPh ? `event-participant-avatar--account-status event-participant-avatar--account-status-${accountPh}` : ''}`.trim()}
                  title={shouldBlur ? '' : displayName}
                >
                  {accountPh ? (
                    <AccountAvatarPlaceholder variant={accountPh} title={accountPhTitle} />
                  ) : avatarUrl ? (
                    <img
                      src={avatarUrl}
                      alt={shouldBlur ? '' : displayName}
                      className={`event-participant-avatar-img ${shouldBlur ? 'blurred' : ''}`}
                    />
                  ) : (
                    <span className={shouldBlur ? 'blurred' : ''}>{initial}</span>
                  )}
                </div>
              );
            })}
            {/* Puste miejsca */}
            {Array.from({ length: Math.max(0, visibleParticipantSlots - participants.length) }).map((_, index) => (
              <div key={`empty-${index}`} className="event-participant-avatar empty">
                +
              </div>
            ))}
            {hiddenParticipantSlots > 0 && (
              <div
                className="event-participant-avatar event-participant-avatar-more"
                title={`Pozostałe miejsca: ${hiddenParticipantSlots}`}
              >
                +{hiddenParticipantSlots}
              </div>
            )}
          </div>
          {membershipType && (
            <span className={`event-membership-chip event-membership-chip--${membershipType}`}>
              <EventCardIcon type={membershipType === 'host' ? 'host' : 'participant'} className="event-membership-chip-icon" />
              <span>{membershipType === 'host' ? 'HOST' : 'UCZESTNIK'}</span>
            </span>
          )}
        </div>

        {/* Cena */}
        <div className="event-card-price">
          {eventData.is_free ? (
            <span className="event-price-free">{t('events.createEvent.isFree')}</span>
          ) : (
            <span className="event-price">
              {eventData.total_cost && eventData.max_participants 
                ? `${(parseFloat(eventData.total_cost) / eventData.max_participants).toFixed(2)} zł`
                : '-'}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

export default EventCard;
